import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { approvalsService } from '../../../services/approvals/approvals.service';
import type { ApprovalPayload, ApprovalReason } from '../../../services/approvals/approvals.service';
import { toHebrewApiErrorMessage } from '../../../services/index/index.service';

type OperationApprovalRequest = { orderId: number; payload: ApprovalPayload } | null;

export function useOperationApprovalRequest(tenantId: string, employeeSession: string | null) {
  const [request, setRequest] = useState<OperationApprovalRequest>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlightRef = useRef(false);

  const open = useCallback((orderId: number, payload: ApprovalPayload) => {
    setRequest({ orderId, payload });
  }, []);

  const close = useCallback(() => {
    if (!inFlightRef.current) setRequest(null);
  }, []);

  const submit = useCallback(async (reason: ApprovalReason, note: string | null) => {
    if (!request || !employeeSession || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await approvalsService.create(tenantId, request.orderId, {
        actionType: request.payload.actionType,
        reason,
        note,
        payload: request.payload,
      }, employeeSession);
      setRequest(null);
      notifications.show({ color: 'blue', title: 'הבקשה נשלחה', message: 'ממתין לאישור מנהל', autoClose: 3000 });
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה בשליחת הבקשה',
        message: toHebrewApiErrorMessage(error instanceof Error ? error.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
      inFlightRef.current = false;
    }
  }, [employeeSession, request, tenantId]);

  return { request, isSubmitting, open, close, submit };
}